import { Alert, Divider, Stack, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { type AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import type { ProductData } from "~/domains/product/types/ProductData";
import ConnectedCPDProgress, {
  getCPDProgressData,
} from "~/features/cms/homepagePersonalization/cpd/components/cpdProgress/ConnectedCPDProgress";
import { getMultipleEntriesRows, shouldRenderMultipleEntriesResult } from "./addToCartResultUtils";
import AddToCartResultSingleItem from "./AddToCartResultSingleItem";
import AddToCartResultsMultipleItems from "./AddToCartResultsMultipleItems";

interface AddToCartResultProps {
  productInfo?: ProductData;
  responseItems: AddToCartResponseItem[];
}

const AddToCartResult = ({ productInfo, responseItems }: AddToCartResultProps) => {
  const { t } = useTranslation();
  const hasMultiple = shouldRenderMultipleEntriesResult({ responseItems });
  const isCPDProgressEnabled = !hasMultiple && window.SCHA.feature.cpdBannerDrawer && !!getCPDProgressData();

  const entries = getMultipleEntriesRows({ responseItems });
  const totalPieces = entries.reduce((acc, e) => acc + e.quantity, 0);

  return (
    <Stack gap={2}>
      {isCPDProgressEnabled ? (
        <Stack gap={2}>
          <Typography variant="titleLarge">{t("addToCart.successMessage")}</Typography>
          <ConnectedCPDProgress />
          <Divider />
        </Stack>
      ) : hasMultiple ? (
        <Alert severity="success">
          {t("addToCart.multipleProductsAdded", { count: entries.length, pieces: totalPieces })}
        </Alert>
      ) : (
        <Alert severity="success">{t("addToCart.successMessage")}</Alert>
      )}

      {hasMultiple ? (
        <AddToCartResultsMultipleItems entries={entries} />
      ) : (
        <AddToCartResultSingleItem productInfo={productInfo} responseItem={responseItems[0]} />
      )}
    </Stack>
  );
};

export default AddToCartResult;
